import {
  Container,
  InnerContainer,
  ScrollableContainer,
  Title,
  CloseBackGround,
  Header,
  OptionsWrapper,
  FilterOptionTitle,
  DropdownContainer,
  DropDownWrapper,
  DropDownIcon,
  DropDownOptionsWrapper,
  SelectedCheckboxLabel,
  DropDownBar,
  DropDownPlaceholder,
  SectionWrapper,
} from './FilterModal.styled';
import { useState } from 'react';
import {
  FilterOption,
  FilterOptions,
} from 'src/components/Tickets/models/Tickets.interface';
import { ChevronDownRegular, DismissRegular } from '@fluentui/react-icons';
import { Checkbox } from '../Checkbox/Checkbox';
import { ModalFooter } from '../ModalFooter/ModalFooter';
import { useServiceDeskContext } from 'src/context/ServiceDesk/ServiceDeskContext';

const filterLabelLookup: Record<keyof FilterOptions, string> = {
  new_tickettype: 'Ticket type',
  _new_category_value: 'Category',
  new_priority: 'Priority',
  new_approvalstatus: 'Approval status',
  statuscode: 'Status',
};

export const FilterModal = () => {
  const {
    filterOptions,
    filterTicketsByMultipleOptions,
    deleteAllFilters,
    mobileFilterToggle,
  } = useServiceDeskContext();
  const [openDropDownId, setOpenDropDownId] = useState<
    keyof FilterOptions | null
  >(null);
  const [filterLocalOptions, setFilterLocalOptions] =
    useState<FilterOptions>(filterOptions);

  const handleDropDownClick = (
    e: React.MouseEvent<HTMLElement>,
    id?: keyof FilterOptions
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenDropDownId((prev) => {
      return prev === id ? null : id;
    });
  };

  const handleCloseDropDown = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenDropDownId(null);
  };

  const handleChangeFilter = (value: string) => {
    const [columnId, label] = value.split(':');
    const clId = columnId as keyof FilterOptions;

    setFilterLocalOptions((prev) => {
      return {
        ...prev,
        [clId]: prev[clId].map((option) => {
          if (option.label === label) {
            return {
              ...option,
              checked: !option.checked,
            };
          }
          return option;
        }),
      };
    });
  };

  const handleMobileFilterClose = () => {
    mobileFilterToggle();
  };

  const handleClearAll = () => {
    deleteAllFilters();
    handleMobileFilterClose();
  };

  const handleClearAllLocalForSingleColumn = (
    e: React.MouseEvent<HTMLElement>,
    columnId: keyof FilterOptions
  ) => {
    e.stopPropagation();
    setFilterLocalOptions((prev) => {
      return {
        ...prev,
        [columnId]: prev[columnId].map((option) => {
          return {
            ...option,
            checked: false,
          };
        }),
      };
    });
    setOpenDropDownId(null);
  };

  const handleFilter = () => {
    filterTicketsByMultipleOptions(filterLocalOptions);
    handleMobileFilterClose();
  };

  return (
    <>
      <CloseBackGround onClick={handleMobileFilterClose} />
      <Container onClick={handleCloseDropDown}>
        <InnerContainer>
          <ScrollableContainer>
            <Header>
              <Title>Filter by</Title>
            </Header>
            <OptionsWrapper>
              {Object.entries(filterLocalOptions)?.map(
                ([columnId, options]) => {
                  const clId = columnId as keyof FilterOptions;
                  const opts = options as FilterOption[];
                  const placeholder = `Select ${filterLabelLookup[
                    clId
                  ].toLocaleLowerCase()}`;

                  const selectedFilterOptions = filterLocalOptions[clId]
                    ?.reduce((acc, curr) => {
                      if (curr.checked) {
                        acc.push(curr.label);
                      }
                      return acc;
                    }, [] as string[])
                    .reverse();
                  const hasSelectedOptions = selectedFilterOptions.length > 0;
                  return (
                    <SectionWrapper key={clId}>
                      <FilterOptionTitle>
                        {filterLabelLookup[clId]}
                      </FilterOptionTitle>
                      <DropdownContainer>
                        <DropDownWrapper
                          className={hasSelectedOptions && 'hasFilters'}
                          onClick={(e) => handleDropDownClick(e, clId)}
                        >
                          <DropDownBar>
                            {selectedFilterOptions.map((label, i, arr) => {
                              const isLast = i === arr.length - 1;
                              return (
                                <SelectedCheckboxLabel key={label}>
                                  {label}
                                  {!isLast ? ', ' : ''}
                                </SelectedCheckboxLabel>
                              );
                            })}
                            {selectedFilterOptions?.length === 0 && (
                              <DropDownPlaceholder>
                                {' '}
                                {placeholder}
                              </DropDownPlaceholder>
                            )}
                          </DropDownBar>

                          {hasSelectedOptions ? (
                            <DropDownIcon
                              onClick={(e) =>
                                handleClearAllLocalForSingleColumn(e, clId)
                              }
                            >
                              <DismissRegular />
                            </DropDownIcon>
                          ) : (
                            <DropDownIcon>
                              <ChevronDownRegular />
                            </DropDownIcon>
                          )}
                        </DropDownWrapper>
                        {openDropDownId === clId && (
                          <DropDownOptionsWrapper
                            onClick={(e) => e.stopPropagation()}
                          >
                            {opts?.map((option) => {
                              return (
                                <Checkbox
                                  key={option.id}
                                  value={`${columnId}:${option.label}`}
                                  checked={option.checked}
                                  label={option.label}
                                  cb={handleChangeFilter}
                                />
                              );
                            })}
                          </DropDownOptionsWrapper>
                        )}
                      </DropdownContainer>
                    </SectionWrapper>
                  );
                }
              )}
            </OptionsWrapper>
          </ScrollableContainer>
          <ModalFooter
            clearAllLabel="Clear all"
            clearAllCB={handleClearAll}
            discardLabel="Discard"
            discardCB={handleMobileFilterClose}
            mainActionLabel="Filter"
            mainActionCB={handleFilter}
          />
        </InnerContainer>
      </Container>
    </>
  );
};
